import type { ChatHistoryState, ChatState } from './interfaces';

export function chatHistoryReducer(state: ChatHistoryState.State, action: ChatState.Action): ChatHistoryState.State {
  return {
    manager: chatHistoryManagerReducer(state.manager, action),
    items: chatHistoryItemsReducer(state.items, action),
    shared: chatHistorySharedReducer(state.shared, action),
    tabs: chatHistoryTabsReducer(state.tabs, action),
  };
}

export function chatHistoryManagerReducer(state: ChatHistoryState.State['manager'], action: ChatState.Action): ChatHistoryState.State['manager'] {
  switch (action.type) {
    case 'instance-context-changed':
      return {
        ...state,
        cursor: null,
        hasMore: false,
        initialized: false,
      };

    case 'chat-history-loaded':
    case 'chat-history-page-loaded':
      return {
        ...state,
        cursor: action.payload.cursor,
        hasMore: action.payload.hasMore,
        loading: false,
        initialized: true,
      };

    case 'chat-history-mode-changed':
      return {
        ...state,
        cursor: null,
        hasMore: false,
        initialized: false,
        mode: action.payload.mode,
      };

    case 'chat-loading-started':
      return {
        ...state,
        loading: true,
        initialized: false,
      };

    case 'chat-loading-errored':
      return {
        ...state,
        loading: false,
        initialized: false,
      };
    case 'chat-brand-changed':
      return {
        ...state,
      };

    default:
      return state;
  }
}

export function chatHistoryItemsReducer(state: ChatHistoryState.State['items'], action: ChatState.Action): ChatHistoryState.State['items'] {

  switch (action.type) {
    case 'instance-context-changed':
    case 'chat-history-mode-changed':
      return [];
    case 'chat-brand-changed':
      return [];

    case 'chat-history-loaded':
      return action.payload.items;

    case 'chat-history-page-loaded':
      return state.concat(action.payload.items);

    case 'chat-instance-removed':
      return state.filter(h => h.id !== action.payload.chatInstance.id);

    case 'chat-instance-renamed': {
      return state.reduce((acc, x) => {
        if (x.id === action.payload.chatInstance.id) {
          return acc.concat({
            ...x,
            name: action.payload.chatInstance.name,
          });
        }

        return acc.concat(x);
      }, []);
    }

    case 'chat-instance-bookmarked': {
      return state.reduce((acc, x) => {
        if (x.id === action.payload.chatInstance.id) {
          return acc.concat({
            ...x,
            bookmarked: action.payload.chatInstance.bookmarked,
          });
        }

        return acc.concat(x);
      }, []);
    }

    case 'new-chat-added':
      return state.concat(action.payload.chatInstance);

    case 'kol-search-submitted':
    case 'query-submitted': {
      const existing = state.find(h => h.identifier === action.payload.chatIdentifier);

      if (existing) {
        return state;
      }

      return [
        {
          id: null,
          identifier: action.payload.chatIdentifier,
          name: 'Placeholder',
          createdOn: new Date(),
          currentQueryIdentifier: null,
          context: null,
          shared: null,
          bookmarked: false,
          filterOverrides: {},
          brandIdentifier: action.payload.brandIdentifier,
        },
        ...state,
      ];
    }

    case 'kol-search-results-available':
    case 'kol-search-profile-available': {
      const existing = state.find(h => h.identifier === action.payload.chatInstance.identifier);

      if (!existing) {
        return [
          action.payload.chatInstance,
          ...state,
        ];
      }

      return state.reduce((acc, item) => {
        if (item.identifier === action.payload.chatInstance.identifier) {
          return acc.concat({
            ...item,
            ...action.payload.chatInstance,
          });
        }
        return acc.concat(item);
      }, []);
    }

    case 'query-response-available': {
      return state.reduce((acc, item) => {
        if (item.identifier === action.payload.chatInstance.identifier) {
          return acc.concat({
            ...item,
            ...action.payload.chatInstance,
          });
        }
        return acc.concat(item);
      }, []);
    }

    case 'chat-loaded': {
      if (action.payload.chatInstance.shared) {
        return state;
      }

      const existing = state.find(h => h.id === action.payload.chatInstance.id);

      if (existing) {
        return state;
      }

      return state.concat(action.payload.chatInstance);
    }

    default:
      return state;
  }

}

export function chatHistorySharedReducer(state: ChatHistoryState.State['shared'], action: ChatState.Action): ChatHistoryState.State['shared'] {
  switch (action.type) {
    case 'instance-context-changed':
    case 'chat-history-mode-changed':
      return [];

    case 'chat-brand-changed':
      return [];

    case 'chat-history-loaded':
      return action.payload.shared;

    case 'chat-history-page-loaded':
      return state.concat(action.payload.shared);

    case 'chat-instance-removed':
      return state.filter(h => h.id !== action.payload.chatInstance.id);

    case 'chat-loaded': {
      if (!action.payload.chatInstance.shared) {
        return state;
      }

      const existing = state.find(h => h.id === action.payload.chatInstance.id);

      if (existing) {
        return state;
      }

      return state.concat(action.payload.chatInstance);
    }

    case 'chat-instance-bookmarked': {
      return state.reduce((acc, x) => {
        if (x.id === action.payload.chatInstance.id) {
          return acc.concat({
            ...x,
            bookmarked: action.payload.chatInstance.bookmarked,
          });
        }

        return acc.concat(x);
      }, []);
    }

    default:
      return state;
  }
}

export function chatHistoryTabsReducer(state: ChatHistoryState.State['tabs'], action: ChatState.Action): ChatHistoryState.State['tabs'] {
  switch (action.type) {
    case 'chat-tabs-index-changed':
      return {
        ...state,
        index: action.payload.index,
      };

    case 'chat-tabs-items-changed': {
      const newItems = action.payload.items;
      const clampedIndex =
        newItems.length === 0
          ? 0
          : Math.min(state.index, newItems.length - 1);

      return {
        ...state,
        items: newItems,
        index: clampedIndex,
      };
    }

    case 'chat-instance-removed': {
      const removedId = action.payload.chatInstance.id;
      const oldItems = state.items;
      const removedIndex = oldItems.findIndex(id => id === removedId);
      const newItems = oldItems.filter(id => id !== removedId);

      let newIndex = state.index;

      if (removedIndex !== -1) {
        if (newItems.length === 0) {
          newIndex = 0;
        } else if (removedIndex < newItems.length) {
          // Prefer the tab that shifted into the removed tab's position.
          newIndex = removedIndex;
        } else {
          // Removed the last tab; move selection to the new last tab.
          newIndex = newItems.length - 1;
        }
      } else if (newIndex >= newItems.length) {
        // Defensive clamping if index is out of bounds after removal.
        newIndex = newItems.length === 0 ? 0 : newItems.length - 1;
      }

      return {
        ...state,
        items: newItems,
        index: newIndex,
      };
    }

    case 'chat-loaded': {
      if (state.items.includes(action.payload.chatInstance.id)) {
        return state;
      }

      const newItems = state.items.concat(action.payload.chatInstance.id);
      const index = newItems.findIndex(id => id === action.payload.chatInstance.id);

      return {
        index,
        items: newItems,
      };
    }

    default:
      return state;
  }
}